// Table — single source of truth for tabular data across the app.
// Used by SourcesList, SourcesArchive, Audit, Workspaces (members + api
// keys). Locks down the visual contract so every table on the project
// reads the same: dense mono header, hover row, horizontal scroll on
// overflow (instead of bursting the page width), sticky header for
// long lists, optional checkbox column.

import React from 'react';

export interface Column<T> {
  /** Stable key used as React key + for sort/filter handlers later. */
  id: string;
  /** Header label. Falls back to '' when headerRender is provided. */
  label: string;
  /** Custom header content. Useful for select-all master checkboxes,
   *  sort affordances, filter dropdowns. When set, replaces label. */
  headerRender?: () => React.ReactNode;
  /** Cell renderer. */
  render: (row: T) => React.ReactNode;
  /** CSS width — px or %. Header + cell get this if set. */
  width?: string | number;
  /** Cell alignment. */
  align?: 'left' | 'right' | 'center';
  /** Use mono font for the cell value (good for IDs, hashes, ip). */
  mono?: boolean;
  /** Truncate cell with ellipsis if it overflows the column. */
  ellipsis?: boolean;
  /** Suppresses the row-click handler when this cell is clicked
   *  (e.g. checkbox col, action col). */
  stopPropagation?: boolean;
}

export interface TableProps<T> {
  columns: Column<T>[];
  rows: T[];
  getRowKey: (row: T) => string;
  /** Whole-row click handler. Hover + cursor: pointer when set. */
  onRowClick?: (row: T) => void;
  /** Optional empty-state element rendered when rows is empty. */
  emptyState?: React.ReactNode;
  /** Tighter row padding for log-like tables (audit, runs). */
  dense?: boolean;
  /** Sticky header within a scrollable container. The wrapping
   *  container becomes the scroll context — pin the table inside a
   *  fixed-height parent for this to do anything useful. */
  stickyHeader?: boolean;
  /** Extra row class — e.g. for archived/paused rows. */
  rowClassName?: (row: T) => string | undefined;
  /** Extra inline row style (opacity for archived, etc.). */
  rowStyle?: (row: T) => React.CSSProperties | undefined;
}

const HEADER_CSS: React.CSSProperties = {
  fontSize: 11,
  fontFamily: 'var(--mono)',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  color: 'var(--ink-3)',
  fontWeight: 500,
  textAlign: 'left',
  whiteSpace: 'nowrap',
};

export function Table<T>(props: TableProps<T>): React.ReactElement {
  const { columns, rows, getRowKey, onRowClick, emptyState, dense, stickyHeader, rowClassName, rowStyle } = props;
  const padding = dense ? '6px 10px' : '8px 12px';

  if (rows.length === 0 && emptyState) {
    return <>{emptyState}</>;
  }

  // Use `table-layout: fixed` whenever any column declares a width.
  // Auto layout ignores `width` once content overflows and pushes the
  // whole table beyond the viewport (audit-log incident 2026-04-28:
  // Detail JSON expanded the row past 3000px and forced horizontal
  // scroll on every screen). Fixed layout pins width-declared cols
  // and gives the rest equal share of the remaining space — long
  // text wraps inside its cell instead of breaking the page.
  const hasFixedWidths = columns.some(c => c.width !== undefined);

  return (
    <div style={{
      border: '1px solid var(--line)',
      borderRadius: 10,
      // Horizontal scroll is the LAST RESORT — keep it as a safety net
      // for very narrow viewports, but the fixed layout below means
      // columns wrap content rather than overflow at desktop widths.
      overflowX: 'auto',
      // sticky header needs a scroll context with a fixed max height
      // so the table body actually scrolls — without it the header
      // 'sticks' to a non-scrolling parent and looks identical.
      ...(stickyHeader ? { maxHeight: '70vh', overflowY: 'auto' as const } : {}),
    }}>
      <table style={{
        width: '100%',
        borderCollapse: 'collapse',
        fontSize: dense ? 12 : 13,
        tableLayout: hasFixedWidths ? 'fixed' : 'auto',
      }}>
        <thead style={{
          background: 'var(--bg-sunken)',
          ...(stickyHeader ? { position: 'sticky' as const, top: 0, zIndex: 1 } : {}),
        }}>
          <tr>
            {columns.map(c => (
              <th
                key={c.id}
                style={{
                  ...HEADER_CSS,
                  padding,
                  width: c.width,
                  textAlign: c.align ?? 'left',
                }}
              >{c.headerRender ? c.headerRender() : c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(row => {
            const key = getRowKey(row);
            const extraStyle = rowStyle?.(row) ?? {};
            const cls = rowClassName?.(row);
            return (
              <tr
                key={key}
                className={cls}
                style={{
                  borderTop: '1px solid var(--line)',
                  cursor: onRowClick ? 'pointer' : 'default',
                  ...extraStyle,
                }}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                onMouseEnter={onRowClick ? e => (e.currentTarget.style.background = 'var(--bg-sunken)') : undefined}
                onMouseLeave={onRowClick ? e => (e.currentTarget.style.background = 'transparent') : undefined}
              >
                {columns.map(c => {
                  // Three rendering modes per cell:
                  //   1. ellipsis=true     → truncate with hover title (single line)
                  //   2. mono+no ellipsis  → wrap with word-break:break-all (long
                  //      tokens like JSON / IDs need to break inside the word)
                  //   3. default           → normal wrap (text content)
                  const overflowStyle: React.CSSProperties = c.ellipsis ? {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  } : c.mono ? {
                    whiteSpace: 'normal',
                    wordBreak: 'break-all',
                    overflowWrap: 'anywhere',
                  } : {
                    whiteSpace: 'normal',
                    overflowWrap: 'break-word',
                  };
                  return (
                    <td
                      key={c.id}
                      onClick={c.stopPropagation ? e => e.stopPropagation() : undefined}
                      style={{
                        padding,
                        width: c.width,
                        textAlign: c.align ?? 'left',
                        fontFamily: c.mono ? 'var(--mono)' : undefined,
                        verticalAlign: 'top',
                        ...overflowStyle,
                      }}
                      title={c.ellipsis && typeof c.render(row) === 'string' ? String(c.render(row)) : undefined}
                    >
                      {c.render(row)}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default Table;
